import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function EditUserDialog({ editing, setEditing, clients, onSaved }) {
  const save = async () => {
    try {
      await api.patch(`/users/${editing.user_id}`, {
        role: editing.role,
        active: editing.active,
        assigned_client_ids: editing.assigned_client_ids || [],
        leave_balances: editing.leave_balances,
        name: editing.name,
      });
      toast.success("Saved");
      setEditing(null);
      onSaved?.();
    } catch { toast.error("Failed"); }
  };

  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
      <DialogContent className="max-w-2xl rounded-sm">
        <DialogHeader><DialogTitle style={{ fontFamily: "Outfit" }}>Edit User · {editing?.name}</DialogTitle></DialogHeader>
        {editing && (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Name</Label>
                <Input className="mt-1 rounded-sm" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={editing.role} onValueChange={(v) => setEditing({ ...editing, role: v })}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Admin">Admin</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                    <SelectItem value="Executive">Executive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="checkbox-active" />
              <span>Account active</span>
            </div>
            <div>
              <Label className="text-xs">Assigned Clients (for Executive access scope)</Label>
              <div className="mt-1 border border-slate-200 rounded-sm p-2 max-h-40 overflow-y-auto space-y-1">
                {clients.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(editing.assigned_client_ids || []).includes(c.id)}
                      onChange={(e) => {
                        const ids = new Set(editing.assigned_client_ids || []);
                        if (e.target.checked) ids.add(c.id); else ids.delete(c.id);
                        setEditing({ ...editing, assigned_client_ids: [...ids] });
                      }}
                      data-testid={`assigned-client-${c.id}`}
                    />
                    {c.name}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Leave Balances (days)</Label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {Object.entries(editing.leave_balances || {})
                  .filter(([k]) => k !== "Leave Without Pay")
                  .map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2">
                      <div className="text-xs flex-1">{k}</div>
                      <Input
                        type="number"
                        className="w-24 rounded-sm font-mono-data"
                        value={v}
                        onChange={(e) => setEditing({ ...editing, leave_balances: { ...editing.leave_balances, [k]: parseFloat(e.target.value) || 0 } })}
                        data-testid={`balance-${k.replace(/\s+/g, "-").toLowerCase()}`}
                      />
                    </div>
                ))}
              </div>
              <div className="mt-2 text-[10px] text-slate-400">
                Note: <span className="font-medium">Leave Without Pay</span> has no quota — it is always available to all employees.
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => setEditing(null)}>Cancel</Button>
          <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={save} data-testid="btn-save-user">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
